import {
  IInventoryOnHandCategoriesContract,
  IInventoryOnHandProductsContract,
} from '@getgreenline/inventory/build/models/inventory'
import { groupBy } from 'lodash'
import { GrPrice } from '../../../../../../utilities/helpers'
import { ColumnKeys, ColumnTitles } from './enums'
import { columnKeyMap } from './maps'
import {
  IInventoryOnHand,
  ICubeProductOnHand,
  ICubeCategoryOnHand,
  TInventoryOnHandColumns,
  IInventoryOnHandCategories,
  IInventoryOnHandSubCategories,
  IInventoryOnHandProducts,
} from './types'

export const columnsWithNumbers: TInventoryOnHandColumns = [
  ColumnKeys.UNIT_COST,
  ColumnKeys.UNIT_PRICE,
  ColumnKeys.TOTAL_COST,
  ColumnKeys.TOTAL_PRICE,
  ColumnKeys.QUANTITY,
  ColumnKeys.MARGIN,
]

const getValFromTitle = (
  inventoryOnHandRaw:
    | IInventoryOnHandProductsContract
    | IInventoryOnHandCategoriesContract
    | ICubeProductOnHand
    | ICubeCategoryOnHand,
  columnTitle: ColumnTitles,
) =>
  inventoryOnHandRaw[columnKeyMap[columnTitle].val] ||
  inventoryOnHandRaw[columnKeyMap[columnTitle].cubeVal]

const getFullCategoryName = (
  productOnHand: IInventoryOnHandProductsContract | ICubeProductOnHand,
) => {
  const parentCategory = getValFromTitle(productOnHand, ColumnTitles.CATEGORY)
  const subCategory = getValFromTitle(productOnHand, ColumnTitles.SUB_CATEGORY)
  if (subCategory) return `${parentCategory} - ${subCategory}`
  if (parentCategory) return parentCategory
  return productOnHand['Categories.fullname']
}

const processProductsOnHandItem = ({
  productOnHand,
  key,
}: {
  productOnHand: IInventoryOnHandProductsContract | ICubeProductOnHand
  key: number
}): IInventoryOnHandProducts => ({
  key: `${key}`,
  locationName: getValFromTitle(productOnHand, ColumnTitles.LOCATION),
  productName: getValFromTitle(productOnHand, ColumnTitles.PRODUCT),
  productSKU: getValFromTitle(productOnHand, ColumnTitles.SKU) || '',
  parentCategoryName: getFullCategoryName(productOnHand) || 'None',
  productVendorName: getValFromTitle(productOnHand, ColumnTitles.BRAND) || '',
  inventoryQuantity: `${Math.floor(getValFromTitle(productOnHand, ColumnTitles.QUANTITY) || 0)}`,
  productCost: GrPrice.convertDollarToDollar(
    getValFromTitle(productOnHand, ColumnTitles.UNIT_COST) || 0,
  ),
  productPrice: GrPrice.convertDollarToDollar(
    getValFromTitle(productOnHand, ColumnTitles.UNIT_PRICE) || 0,
  ),
  inventoryCost: GrPrice.convertDollarToDollar(
    getValFromTitle(productOnHand, ColumnTitles.TOTAL_COST) || 0,
  ),
  inventoryPrice: GrPrice.convertDollarToDollar(
    getValFromTitle(productOnHand, ColumnTitles.TOTAL_PRICE) || 0,
  ),
  margin: `${Number(getValFromTitle(productOnHand, ColumnTitles.MARGIN) || 0).toFixed(2)}%`,
})

export const processProductsOnHandContract = (
  productsOnHand: Array<ICubeProductOnHand | IInventoryOnHandProductsContract>,
) =>
  productsOnHand.map((productOnHand, i) => processProductsOnHandItem({ productOnHand, key: i + 1 }))

export const groupCategoriesOnHand = (categoriesOnHand: ICubeCategoryOnHand[], nested: boolean) => {
  if (nested) {
    return groupItemsInNestedCategory(categoriesOnHand)
  }

  return Object.values(groupBy(categoriesOnHand, 'Categories.parentname')).reduce(
    (
      categoriesOnHandGrouped: IInventoryOnHandCategoriesContract[],
      categoryOnHandGrouped,
    ): IInventoryOnHandCategoriesContract[] => {
      categoriesOnHandGrouped.push(groupItemsInCategory(categoryOnHandGrouped))
      return categoriesOnHandGrouped
    },
    [],
  )
}

const processCategoriesOnHandItem = ({
  categoryOnHand,
  key,
}: {
  categoryOnHand: IInventoryOnHandCategoriesContract | ICubeCategoryOnHand
  key: number
}): IInventoryOnHandCategories => ({
  key: `${key}`,
  parentCategoryName: getValFromTitle(categoryOnHand, ColumnTitles.CATEGORY) || 'None',
  inventoryQuantity: `${Math.floor(getValFromTitle(categoryOnHand, ColumnTitles.QUANTITY) || 0)}`,
  inventoryCost: GrPrice.convertDollarToDollar(
    getValFromTitle(categoryOnHand, ColumnTitles.TOTAL_COST) || 0,
  ),
  inventoryPrice: GrPrice.convertDollarToDollar(
    getValFromTitle(categoryOnHand, ColumnTitles.TOTAL_PRICE) || 0,
  ),
  margin: `${Number(getValFromTitle(categoryOnHand, ColumnTitles.MARGIN) || 0).toFixed(2)}%`,
})

export const processCategoriesOnHandContract = (
  categoriesOnHand: Array<ICubeCategoryOnHand | IInventoryOnHandCategoriesContract>,
) =>
  categoriesOnHand.map((categoryOnHand, i) =>
    processCategoriesOnHandItem({ categoryOnHand, key: i + 1 }),
  )

const getSubCategoryValue = (
  categoryOnHand: IInventoryOnHandCategoriesContract | ICubeCategoryOnHand,
): string => {
  const parentCategory = getValFromTitle(categoryOnHand, ColumnTitles.CATEGORY)
  const category = getValFromTitle(categoryOnHand, ColumnTitles.SUB_CATEGORY)
  return parentCategory !== category ? category : ''
}
const processSubCategoriesOnHandItem = ({
  categoryOnHand,
  key,
}: {
  categoryOnHand: IInventoryOnHandCategoriesContract | ICubeCategoryOnHand
  key: number
}): IInventoryOnHandSubCategories => ({
  key: `${key}`,
  parentCategoryName: getValFromTitle(categoryOnHand, ColumnTitles.CATEGORY) || 'None',
  categoryName: getSubCategoryValue(categoryOnHand),
  inventoryQuantity: `${Math.floor(getValFromTitle(categoryOnHand, ColumnTitles.QUANTITY) || 0)}`,
  inventoryCost: GrPrice.convertDollarToDollar(
    getValFromTitle(categoryOnHand, ColumnTitles.TOTAL_COST) || 0,
  ),
  inventoryPrice: GrPrice.convertDollarToDollar(
    getValFromTitle(categoryOnHand, ColumnTitles.TOTAL_PRICE) || 0,
  ),
  margin: `${Number(getValFromTitle(categoryOnHand, ColumnTitles.MARGIN) || 0).toFixed(2)}%`,
})

export const processSubCategoriesOnHandContract = (
  categoriesOnHand: Array<ICubeCategoryOnHand | IInventoryOnHandCategoriesContract>,
) =>
  categoriesOnHand.map((categoryOnHand, i) =>
    processSubCategoriesOnHandItem({ categoryOnHand, key: i + 1 }),
  )

const removeSymbols = (s: string) => s.replace('$', '').replace(/,/g, '').replace('%', '')
const sortString = (a: string, b: string) => (a < b ? -1 : 1)
const sortNumericString = (a: string, b: string) => (Number(a) < Number(b) ? -1 : 1)

export const calculateTotalFrom = ({
  inventoryOnHand,
  key,
}: {
  inventoryOnHand: IInventoryOnHand[]
  key: keyof IInventoryOnHand
}) => {
  const total = inventoryOnHand.reduce(
    (total, inventoryItem) => total + Number(removeSymbols(inventoryItem[key])),
    0,
  )
  return key !== ColumnKeys.QUANTITY ? GrPrice.convertDollarToDollar(total) : `${Math.floor(total)}`
}

export const getSorter =
  (columnDataKey: keyof IInventoryOnHand) => (a: IInventoryOnHand, b: IInventoryOnHand) => {
    const columnA = a[columnDataKey] && removeSymbols(a[columnDataKey])
    const columnB = b[columnDataKey] && removeSymbols(b[columnDataKey])
    if (!columnA) return 1
    if (!columnB) return -1
    if (isNaN(Number(columnA))) return sortString(columnA, columnB)
    return sortNumericString(columnA, columnB)
  }

export const isToday = (date: Date) => {
  const now = new Date()
  const today = `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}`
  const dateFormatted = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
  return dateFormatted === today
}

const calculateMarginPercent = (price: number, cost: number) => {
  if (price === 0) {
    return 0
  }

  return ((price - cost) / price) * 100
}

const groupItemsInCategory = (
  categoryOnHandGrouped: ICubeCategoryOnHand[],
): IInventoryOnHandCategoriesContract => {
  const firstItem = categoryOnHandGrouped[0]
  const categoryOnHandRaw: IInventoryOnHandCategoriesContract = {
    parentCategoryName: firstItem['Categories.parentname'],
    categoryName: firstItem['Categories.name'],
    inventoryQuantity: 0,
    inventoryCost: 0,
    inventoryPrice: 0,
    margin: 0,
  }
  categoryOnHandGrouped.forEach((categoryOnHand) => {
    categoryOnHandRaw.inventoryQuantity += categoryOnHand['InventoryLog.newValue']
    categoryOnHandRaw.inventoryCost += categoryOnHand['InventoryLog.cost']
    categoryOnHandRaw.inventoryPrice += categoryOnHand['InventoryLog.price']
  })

  categoryOnHandRaw.margin = calculateMarginPercent(
    categoryOnHandRaw.inventoryPrice,
    categoryOnHandRaw.inventoryCost,
  )

  return categoryOnHandRaw
}

const groupItemsInNestedCategory = (
  categoriesOnHand: ICubeCategoryOnHand[],
): IInventoryOnHandCategoriesContract[] => {
  const mappedCategoriesOnHand = new Map<string, IInventoryOnHandCategoriesContract>()

  categoriesOnHand.forEach((categoryOnHand) => {
    const key = `${categoryOnHand['Categories.parentname']} - ${categoryOnHand['Categories.name']}`

    const categoryOnHandRaw: IInventoryOnHandCategoriesContract = mappedCategoriesOnHand.get(
      key,
    ) || {
      parentCategoryName: categoryOnHand['Categories.parentname'],
      categoryName: categoryOnHand['Categories.name'],
      inventoryQuantity: 0,
      inventoryCost: 0,
      inventoryPrice: 0,
      margin: 0,
    }

    categoryOnHandRaw.inventoryQuantity += categoryOnHand['InventoryLog.newValue']
    categoryOnHandRaw.inventoryCost += categoryOnHand['InventoryLog.cost']
    categoryOnHandRaw.inventoryPrice += categoryOnHand['InventoryLog.price']

    categoryOnHandRaw.margin = calculateMarginPercent(
      categoryOnHandRaw.inventoryPrice,
      categoryOnHandRaw.inventoryCost,
    )

    mappedCategoriesOnHand.set(key, categoryOnHandRaw)
  })

  return Array.from(mappedCategoriesOnHand.values())
}

export const removeDollarSign = (inventoryOnHand: IInventoryOnHand[]) =>
  inventoryOnHand.map((row) => {
    const newRow = { ...row }

    Object.keys(newRow).forEach((key) => {
      switch (key) {
        case ColumnKeys.UNIT_COST:
        case ColumnKeys.TOTAL_COST:
        case ColumnKeys.UNIT_PRICE:
        case ColumnKeys.TOTAL_PRICE:
          newRow[key] = newRow[key].replace('$', '')
          break

        default:
          break
      }
    })

    return newRow
  })
